import { theme } from '@notino/react-styleguide'
import { Rating } from '@smastrom/react-rating'
import styled, { css } from 'styled-components'
import '@smastrom/react-rating/style.css'
import { BREAKPOINTS } from '../../styles/constants'

export const ProgressBarContainer = styled.div`
	display: flex;
	align-items: center;
	width: calc(100% - 2rem); // 100% - space for the label
`

export const ProgressBar = styled.div<{ $width: string; $height: string }>`
	display: flex;
	width: ${({ $width }) => $width};
	height: ${({ $height }) => $height};
`

export const Segment = styled.div<{
	$width: string
	$height: string
	$backgroundColor: string
	$marginRight: string
}>`
	width: ${({ $width }) => $width};
	height: ${({ $height }) => $height};
	background-color: ${({ $backgroundColor }) => $backgroundColor};
	margin-right: ${({ $marginRight }) => $marginRight};

	position: relative;
	overflow: hidden;

	&:last-child {
		margin-right: 0;
		border-top-right-radius: 9999px;
		border-bottom-right-radius: 9999px;
	}

	&:first-child {
		border-top-left-radius: 9999px;
		border-bottom-left-radius: 9999px;
	}
`

export const FilledPart = styled.div<{ $width: string; $filledColor: string; $isFirst: boolean; $isLast: boolean }>`
	width: ${({ $width }) => $width};
	height: 100%;
	background-color: ${({ $filledColor }) => $filledColor};
	position: absolute;
	top: 0;
	left: 0;

	${({ $isFirst }) =>
		$isFirst &&
		css`
			border-top-left-radius: 9999px;
			border-bottom-left-radius: 9999px;
		`}

	${({ $isLast }) =>
		$isLast &&
		css`
			border-top-right-radius: 9999px;
			border-bottom-right-radius: 9999px;
		`}
`

export const RatingsList = styled.div<{ $twoColLayoutOnDesktop: boolean }>`
	display: grid;
	row-gap: 0.5rem;

	${({ $twoColLayoutOnDesktop }) =>
		$twoColLayoutOnDesktop &&
		css`
			@media (min-width: ${BREAKPOINTS.md}) {
				grid-template-columns: 1fr 1fr;
				column-gap: 3rem;
			}
		`}
`

export const RatingsDescriptionList = styled.dl<{ $twoColLayoutOnDesktop: boolean }>`
	display: grid;
	row-gap: 0.5rem;
	margin: 0;

	${({ $twoColLayoutOnDesktop }) =>
		$twoColLayoutOnDesktop &&
		css`
			@media (min-width: ${BREAKPOINTS.md}) {
				grid-template-columns: 1fr 1fr;
				column-gap: 3rem;
			}
		`}
`

export const RatingRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1rem;
`

export const RatingDescriptionRow = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1rem;
`

export const Rate = styled(Rating)<{
	$space: number
	$width: string
	$height: string
	$minHitHeight?: string
	$filledColor: string
	$backgroundColor: string
}>`
	${({ $space, $width, $height, $minHitHeight = '0.5rem', $filledColor, $backgroundColor }) => css`
		gap: ${$space}px;
		flex-shrink: 0;
		width: calc(${$width} - 1.5rem); // 100% - space for the label
		min-height: 1rem;
		display: flex;
		align-items: center;
		border-radius: 9999px;

		&.rr--focus-reset {
			outline: 2px solid ${theme.color.text.primary};
		}

		.rr--box {
			width: 20%;
			min-height: ${$minHitHeight};
			height: auto;
			position: relative;
			background-color: transparent;

			&::before {
				content: '';
				position: absolute;
				left: 0;
				right: 0;
				top: 50%;
				transform: translateY(-50%);
				height: ${$height};
				transition: background-color 0.3s ease;
				pointer-events: none;
			}

			&.rr--on::before {
				background-color: ${$filledColor};
			}

			&.rr--off::before {
				background-color: ${$backgroundColor};
			}

			&:focus-visible {
				outline-offset: 1px;
				outline: 2px solid ${theme.color.text.primary};
			}

			svg {
				display: none;
			}

			&:first-child::before {
				border-top-left-radius: 9999px;
				border-bottom-left-radius: 9999px;
			}

			/* We cannot use :last-child because there is still .rr--reset after the last .rr--box */
			&:not(:has(~ .rr--box))::before {
				border-top-right-radius: 9999px;
				border-bottom-right-radius: 9999px;
			}
		}
	`}
`

export const RatingLabel = styled.span`
	${theme.typography.labelRegular400};
	color: ${theme.color.text.secondary};
	display: inline-block;
`

export const RatingTerm = styled.dt`
	${theme.typography.labelRegular400};
	color: ${theme.color.text.secondary};
	margin: 0;
`

export const LabelCol = styled.div``

export const RatingCol = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
`

export const RatingDescription = styled.dd`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
	margin: 0;
	position: relative;
`

export const RatingValue = styled.span`
	${theme.typography.labelSmall400};
	text-align: right;
`
